import * as Sentry from '@sentry/react';
import { getOriginPrivateDirectory } from 'native-file-system-adapter';
import React, { useEffect } from 'react';

import { bannerToast } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';

import { SWAP_FILE_EXT, readAppointmentFilesFromOpfs } from './opfs';
import {
  useUserFileSystemStore,
  useUserFileSystemStoreApi,
} from './user-file-system.store';
import { hasAttemptedUserFileSystemReload } from './userFileSystemReloadAttempt';

export interface UserFileSystemInitializerProps {
  children: React.ReactNode;
}

export const UserFileSystemInitializer: React.FC<
  UserFileSystemInitializerProps
> = ({ children }) => {
  const {
    userId,
    tenantId,
    userFileSystem: storage,
  } = useUserFileSystemStore((state) => state);
  const storeApi = useUserFileSystemStoreApi();

  const logger = useNamedLogger('UserFileSystemInitializer');

  useEffect(
    function attemptInitLocalStorage() {
      const init = async () => {
        try {
          // This is ponyfill for Safari. If createWritable is not available, it will add it
          await getOriginPrivateDirectory();

          const opfsRoot = await navigator.storage.getDirectory();
          const userDir = await opfsRoot.getDirectoryHandle(userId, {
            create: true,
          });

          const tenantDir = await userDir.getDirectoryHandle(tenantId, {
            create: true,
          });

          const appointmentsDir = await tenantDir.getDirectoryHandle(
            'appointments',
            {
              create: true,
            },
          );

          storeApi.setState({
            userFileSystem: {
              type: 'available',
              topLevelUserDir: userDir,
              tenantDir,
              appointmentsDir,
            },
          });
        } catch (error) {
          Sentry.captureException(error);

          logger.warn(
            'Failed to initialize user file system',
            error instanceof DOMException
              ? {
                  error,
                  errorName: error.name,
                  errorMessage: error.message,
                  errorCode: error.code,
                }
              : {
                  error,
                  errorMessage: (error as Error)?.message,
                },
          );

          storeApi.setState({
            userFileSystem: {
              type: 'unavailable',
            },
          });

          // Only surface the legacy banner once a reload has already been
          // attempted — otherwise UserFileSystemReloadGuard's countdown toast
          // owns the first-encounter UX and we don't want both visible.
          if (hasAttemptedUserFileSystemReload(userId)) {
            logger.info(
              'Notifying user of: unavailable file system (post-reload)',
            );
            bannerToast.warning("Can't Save Data", {
              id: 'file-system-unavailable',
              description:
                "This usually happens in Private/Incognito mode. Please switch to a regular browser window. If you're already in one, try restarting your browser or device.",
            });
          } else {
            logger.info(
              'Suppressing legacy unavailable-file-system banner: no prior reload attempt recorded',
            );
          }
        }
      };

      init();
    },
    [storage.type, userId, storeApi, tenantId, logger],
  );

  useEffect(
    function reportLocalFileStats() {
      let isRunning = false;
      const interval = setInterval(
        async () => {
          try {
            if (isRunning) {
              return;
            }
            isRunning = true;

            if (storage.type === 'available') {
              const filePromises: Promise<{
                localFileCount: number;
                localSwapFileCount: number;
              }>[] = [];

              const emptyDirPromises: Promise<void>[] = [];
              const scanStart = new Date();

              for await (const [id, handle] of storage.appointmentsDir) {
                if (handle.kind === 'directory') {
                  const promise = async () => {
                    let localFileCount = 0;
                    let localSwapFileCount = 0;
                    const appointmentFilesResult =
                      await readAppointmentFilesFromOpfs({
                        userFileSystem: storage,
                        appointmentId: id,
                        logger,
                      });

                    if (
                      appointmentFilesResult.type === 'directoryExists' &&
                      appointmentFilesResult.files.length
                    ) {
                      for (const file of appointmentFilesResult.files) {
                        if (file.name.endsWith(SWAP_FILE_EXT)) {
                          localSwapFileCount++;
                        } else {
                          localFileCount++;
                        }
                      }
                    } else {
                      // Remove empty directories
                      emptyDirPromises.push(
                        storage.appointmentsDir.removeEntry(id).catch((e) => {
                          logger.warn('Failed to remove empty directory', {
                            name: id,
                            error: (e as Error)?.message,
                          });
                        }),
                      );
                    }

                    return {
                      localFileCount,
                      localSwapFileCount,
                    };
                  };

                  filePromises.push(promise());
                }
              }

              let localFileCount = 0;
              let localSwapFileCount = 0;
              const results = await Promise.all(filePromises);
              for (const result of results) {
                localFileCount += result.localFileCount;
                localSwapFileCount += result.localSwapFileCount;
              }

              const scanEnd = new Date();

              logger.info('Local file stats', {
                localFileCount,
                localSwapFileCount,
                emptyDirsToRemove: emptyDirPromises.length,
                scanDuration: scanEnd.getTime() - scanStart.getTime(),
              });
            }
          } catch (error) {
            // Best-effort background telemetry: the every-10-min scan races
            // against live OPFS writes/deletes, so a handle can go stale
            // (InvalidStateError) or an entry can vanish (NotFoundError)
            // mid-scan. Those are expected, self-heal next tick, and log at
            // warn. Anything else is unexpected and stays at error. [ENG-5267]
            const isTransientOpfsRace =
              error instanceof DOMException &&
              (error.name === 'NotFoundError' ||
                error.name === 'InvalidStateError');

            const context = {
              error: (error as Error)?.message,
              errorName: error instanceof DOMException ? error.name : undefined,
            };

            if (isTransientOpfsRace) {
              logger.warn('Error while reporting local file stats', context);
            } else {
              logger.error('Error while reporting local file stats', context);
            }
          }

          isRunning = false;
        },
        // Report file stats every 10 minutes
        // Offset by 3333ms to reduce collisions with the interval running
        // in PeriodicFileSyncProvider.
        // TODO(jesse)[ENG-1373] We won't need a weird offset once we consolidate
        1000 * 60 * 10 + 3333,
      );

      return () => {
        clearInterval(interval);
      };
    },
    [storage, logger],
  );

  if (storage.type === 'pending') {
    return null;
  }

  return children;
};
